import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Award, TrendingUp } from "lucide-react";
import { topScorers, topAssists } from "../../data/arsenalData";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#061930] border border-[#DB0007] p-3 font-outfit text-sm">
      <div className="font-anton uppercase text-white tracking-widest text-xs">{label}</div>
      <div className="text-[#9C824A] mt-1">{payload[0].value} {payload[0].dataKey === "goals" ? "goles" : "asistencias"}</div>
    </div>
  );
};

export const Stats = () => {
  return (
    <section
      id="stats"
      data-testid="stats-section"
      className="relative bg-[#061930] text-white py-24 md:py-32 overflow-hidden"
    >
      {/* Tactical grid background */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
      </div>
      <div className="absolute top-0 left-1/2 w-1 h-24 bg-gradient-to-b from-[#DB0007] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="section-overline justify-center">Estadísticas</div>
          <h2 className="font-anton uppercase text-4xl sm:text-5xl lg:text-6xl mt-6 tracking-tight">
            Los números <span className="text-[#DB0007]">hablan</span>
          </h2>
          <p className="font-outfit text-white/60 mt-4 max-w-2xl mx-auto">
            Temporada 2026 — el rendimiento individual detrás del éxito colectivo.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 min-w-0">
          {/* Top Scorers */}
          <StatChart
            title="Goleadores"
            icon={<Award size={18} />}
            data={topScorers.map(p => ({ name: p.nickname, goals: p.goals, fullName: p.name }))}
            dataKey="goals"
            color="#DB0007"
            testId="scorers-chart"
          />

          {/* Top Assists */}
          <StatChart
            title="Asistencias"
            icon={<TrendingUp size={18} />}
            data={topAssists.map(p => ({ name: p.nickname, assists: p.assists, fullName: p.name }))}
            dataKey="assists"
            color="#9C824A"
            testId="assists-chart"
          />
        </div>

        {/* Player list cards */}
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {topScorers.slice(0, 5).map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-[#0a2240] border border-white/10 p-5 hover:border-[#DB0007] transition-all group"
              data-testid={`player-card-${i}`}
            >
              <div className="flex items-start justify-between">
                <div className="font-mono-jb text-xs text-[#9C824A]">#{i + 1}</div>
                <div className="font-anton text-3xl text-[#DB0007] leading-none">
                  {p.goals}
                </div>
              </div>
              <div className="mt-6">
                <div className="font-anton uppercase text-white text-lg leading-tight">
                  "{p.nickname}"
                </div>
                <div className="font-outfit text-white/60 text-sm mt-1">
                  {p.name}
                </div>
              </div>
              <div className="mt-4 h-1 bg-white/5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${(p.goals / topScorers[0].goals) * 100}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.4 + i * 0.1 }}
                  className="h-full bg-[#DB0007]"
                />
              </div>
              <div className="mt-2 font-mono-jb text-[10px] uppercase tracking-widest text-white/40">
                {p.matches} PJ · {(p.goals / p.matches).toFixed(2)} g/p
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const StatChart = ({ title, icon, data, dataKey, color, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7 }}
    className="bg-[#0a2240] border border-white/10 p-6 sm:p-8 min-w-0"
    data-testid={testId}
  >
    <div className="flex items-center gap-3 mb-6">
      <div
        className="w-10 h-10 flex items-center justify-center"
        style={{ background: color, color: "#fff" }}
      >
        {icon}
      </div>
      <div>
        <h3 className="font-anton uppercase text-2xl tracking-tight">{title}</h3>
        <div className="font-mono-jb text-[10px] uppercase tracking-widest text-white/40">
          Top 5 · Temporada 2026
        </div>
      </div>
    </div>
    <div className="h-72 w-full min-h-[18rem] min-w-0">
      <ResponsiveContainer width="100%" height="100%" minHeight={288} minWidth={0}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.08)" />
          <XAxis
            dataKey="name"
            tick={{ fill: "#fff", fontSize: 12, fontFamily: "Outfit" }}
            axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11, fontFamily: "JetBrains Mono" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.05)" }} />
          <Bar dataKey={dataKey} radius={[0, 0, 0, 0]}>
            {data.map((_, i) => (
              <Cell key={i} fill={color} fillOpacity={1 - i * 0.12} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </motion.div>
);
